import { MouseEventHandler, ReactNode, useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'

import { useLenis } from '@/contexts/lenis'

import SVGIcon from './svg-icon'
import { cn } from '@/lib/utils'

const Button = ({
  onClick,
  children
}: {
  onClick: MouseEventHandler<HTMLButtonElement>
  children: ReactNode
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex size-[64px] items-center justify-center',
        'bg-border-primary-dark text-text-button-primary-dark dark:bg-border-primary-light dark:text-text-button-primary-light'
      )}
    >
      {children}
    </button>
  )
}

const OverlayGallery = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [imageActive, setImageActive] = useState(0)
  const [images, setImages] = useState<HTMLImageElement[]>([])

  const { lenis } = useLenis()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true
  })

  // Centro embla
  useEffect(() => {
    if (!emblaApi) return

    emblaApi.scrollTo(imageActive)
  }, [imageActive, emblaApi])

  // Inizializza immagini e registra eventi click solo una volta
  useEffect(() => {
    if (typeof window === 'undefined') return

    const nodeList = document.querySelectorAll<HTMLImageElement>('[data-overlay-image]')
    const imgArray = Array.from(nodeList)

    imgArray.forEach((image, index) => {
      image.addEventListener('click', () => {
        setImageActive(index)
        setIsOpen(true)
      })
    })

    setImages(imgArray)
  }, [])

  // Callback navigazione
  const handleNext = useCallback(() => {
    setImageActive((prev) => (prev + 1) % images.length)
  }, [images.length])

  const handlePrev = useCallback(() => {
    setImageActive((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  // Gestione eventi tastiera
  useEffect(() => {
    if (!isOpen) return

    lenis?.stop()

    const handleKeydown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          setIsOpen(false)
          break
        case 'ArrowRight':
          handleNext()
          break
        case 'ArrowLeft':
          handlePrev()
          break
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => {
      window.removeEventListener('keydown', handleKeydown)
      lenis?.start()
    }
  }, [isOpen, handleNext, handlePrev, lenis])

  // Render
  if (images.length === 0) return null

  return (
    <div
      className={cn(
        'overlay-gallery pointer-events-none fixed left-0 top-0 z-overlay h-full w-full',
        'ease-in-out opacity-0 transition-opacity duration-300',
        {
          'pointer-events-auto opacity-100': isOpen
        }
      )}
      data-overlay-gallery
    >
      <div
        className="absolute inset-0 z-0 bg-surface-primary-light opacity-80 dark:bg-surface-primary-dark"
        onClick={() => setIsOpen(false)}
      />

      <div className="absolute inset-0 z-10 bg-surface-primary-dark md:inset-spacing-md dark:bg-surface-primary-light">
        <div
          className={cn(
            'pointer-events-none absolute left-0 top-0 z-10 w-full p-spacing-sm text-center',
            'flex items-center justify-center gap-spacing-2xs',
            'md:left-auto md:right-0 md:h-full md:w-auto md:flex-col md:p-spacing-md',
            { '[&_button]:pointer-events-auto': isOpen }
          )}
        >
          <Button onClick={() => setIsOpen(false)}>
            <SVGIcon symbol="close-icon" className="size-[16px] fill-current" />
          </Button>
          <Button onClick={handlePrev}>
            <SVGIcon symbol="prev-icon" className="size-[16px] fill-current" />
          </Button>
          <Button onClick={handleNext}>
            <SVGIcon symbol="next-icon" className="size-[16px] fill-current" />
          </Button>
        </div>

        <div className={cn('absolute inset-0 z-0 overflow-hidden')}>
          {images.map((image, i) => (
            <Image
              key={i}
              src={image.src}
              alt={image.alt || ''}
              fill
              className={cn(
                'absolute left-0 top-0 h-[100%] w-[100%] object-contain',
                'pointer-events-none opacity-0 transition-opacity duration-300',
                {
                  'pointer-events-auto opacity-100': imageActive === i && isOpen
                }
              )}
            />
          ))}
        </div>

        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 m-auto max-w-[calc(var(--slide-size)*6)]'
          )}
        >
          <div
            className="embla m-spacing-md"
            style={
              {
                '--slide-spacing': '8px'
              } as React.CSSProperties & Record<string, string>
            }
          >
            <div className="embla__viewport overflow-hidden" ref={emblaRef}>
              <div className="embla__container ml-[calc(-1*var(--slide-spacing))] flex touch-pan-y touch-pinch-zoom">
                {images.map((image, i) => (
                  <button
                    className="embla__slide group relative ml-[var(--slide-spacing)] h-[var(--slide-size)] min-w-0 flex-[0_0_var(--slide-size)] bg-surface-primary-dark"
                    key={i}
                    onClick={() => setImageActive(i)}
                  >
                    <Image
                      src={image.src}
                      alt={image.alt || ''}
                      fill
                      className={cn(
                        'opacity-40 transition-opacity duration-300 group-hover:opacity-80',
                        {
                          'opacity-100': imageActive === i
                        }
                      )}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OverlayGallery
